// Modifica ticket esistente
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Alert } from '../../components/ui/Alert'
import { Skeleton } from '../../components/ui/Skeleton'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { useTicket, useDeleteTicket } from '../../hooks/useTickets'
import { ticketKeys } from '../../hooks/useTickets'
import api from '../../lib/api'

const schema = z.object({
  title: z.string().min(5, 'Titolo troppo corto').max(200),
  description: z.string().optional(),
  type: z.enum(['fault', 'scheduled_maintenance', 'inspection', 'installation', 'testing', 'urgent']),
  priority: z.enum(['low', 'medium', 'high', 'critical']),
  internalNotes: z.string().optional(),
  customerNotes: z.string().optional(),
})

type FormData = z.infer<typeof schema>

export function EditTicketPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: ticket, isLoading } = useTicket(id ?? '')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const deleteMutation = useDeleteTicket()

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  // Popola il form quando il ticket è caricato
  useEffect(() => {
    if (ticket) {
      reset({
        title: ticket.title,
        description: ticket.description ?? '',
        type: ticket.type as FormData['type'],
        priority: ticket.priority as FormData['priority'],
        internalNotes: (ticket as unknown as { internalNotes?: string }).internalNotes ?? '',
        customerNotes: (ticket as unknown as { customerNotes?: string }).customerNotes ?? '',
      })
    }
  }, [ticket, reset])

  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      const res = await api.patch(`/tickets/${id}`, data)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ticketKeys.detail(id ?? '') })
      void qc.invalidateQueries({ queryKey: ticketKeys.lists() })
      navigate(`/tickets/${id}`)
    },
  })

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <Skeleton height={48} className="rounded-lg" />
        <Skeleton height={300} className="rounded-lg" />
      </div>
    )
  }

  if (!ticket) {
    return <Alert variant="error">Ticket non trovato.</Alert>
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <PageHeader
        title={`Modifica ${ticket.ticketNumber}`}
        breadcrumb={[
          { label: 'Ticket', href: '/tickets' },
          { label: ticket.ticketNumber, href: `/tickets/${id}` },
          { label: 'Modifica' },
        ]}
      />

      {mutation.error && (
        <Alert variant="error">Errore durante il salvataggio. Riprova.</Alert>
      )}

      <form onSubmit={handleSubmit((data) => mutation.mutate(data))} noValidate>
        <Card>
          <div className="space-y-4">
            <Input
              label="Titolo"
              required
              error={errors.title?.message}
              {...register('title')}
            />

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Tipo intervento"
                required
                options={[
                  { value: 'fault', label: 'Guasto' },
                  { value: 'scheduled_maintenance', label: 'Manutenzione programmata' },
                  { value: 'installation', label: 'Installazione' },
                  { value: 'inspection', label: 'Ispezione' },
                  { value: 'testing', label: 'Test' },
                  { value: 'urgent', label: 'Urgente' },
                ]}
                value={watch('type')}
                onValueChange={(v) => setValue('type', v as FormData['type'])}
                error={errors.type?.message}
              />
              <Select
                label="Priorità"
                required
                options={[
                  { value: 'low', label: 'Bassa' },
                  { value: 'medium', label: 'Media' },
                  { value: 'high', label: 'Alta' },
                  { value: 'critical', label: 'Critica' },
                ]}
                value={watch('priority')}
                onValueChange={(v) => setValue('priority', v as FormData['priority'])}
                error={errors.priority?.message}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
                Descrizione
              </label>
              <textarea
                rows={4}
                className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-subtle)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)] resize-none"
                {...register('description')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
                Device Guasto
              </label>
              <textarea
                rows={3}
                placeholder="Note visibili solo allo staff..."
                className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-subtle)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)] resize-none"
                {...register('internalNotes')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
                Note cliente
              </label>
              <textarea
                rows={3}
                placeholder="Note visibili al cliente nel portale..."
                className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-subtle)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)] resize-none"
                {...register('customerNotes')}
              />
            </div>
          </div>
        </Card>

        <div className="flex items-center justify-between mt-4">
          <Button
            type="button"
            variant="danger"
            leftIcon={<Trash2 className="h-4 w-4" />}
            onClick={() => setConfirmDelete(true)}
          >
            Elimina
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={() => navigate(`/tickets/${id}`)}>
              Annulla
            </Button>
            <Button type="submit" isLoading={mutation.isPending}>
              Salva modifiche
            </Button>
          </div>
        </div>
      </form>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() =>
          deleteMutation.mutate(id ?? '', {
            onSuccess: () => navigate('/tickets'),
          })
        }
        title="Elimina ticket"
        message={`Eliminare il ticket ${ticket.ticketNumber}? L'operazione non è reversibile.`}
        confirmLabel="Elimina"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
